import { useEffect, useMemo, useState } from "react";
import { Typography, Box, Button, Stack, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import dayjs from "dayjs";
import { Check, CheckCircle } from "@mui/icons-material";
import convertCurrency from "src/helper/convertCurrency";
import FormControlAutocomplete from "src/components/shared/FormControlAutocomplete";
import usePaymentMethodList from "src/hooks/payment-method/usePaymentMethodList";
import { TRANSPORTATION_TYPE } from "src/constant";
import useTPList from "src/hooks/setting/transportation-provider/useTPList";
import { useFormContext } from "react-hook-form";
import FormControlNumericInput from "src/components/shared/FormControlNumericInput";

const RequiredLabel = ({ children, required = true }) => (
  <span>
    {children}
    {required && <span style={{ color: "red", marginLeft: 2 }}>*</span>}
  </span>
);

const PICK_UP_OPTIONS = [
  {
    type: TRANSPORTATION_TYPE.PICKUP,
    title: "Lấy hàng",
    description: "ĐVVC sẽ đến địa chỉ của bạn để lấy hàng",
  },
  {
    type: TRANSPORTATION_TYPE.SELFDELIVERY,
    title: "Gửi hàng tại bưu cục",
    description: "Bạn có thể gửi ở bất kì bưu cục: ",
    subText: "Bưu cục gần nhất",
  },
];

const PickUpCard = ({ option, isSelected, onClick, warehouse }) => {
  const color = isSelected ? "var(--success-txt)" : "var(--black)";
  const colorSx = { color };

  return (
    <Box
      onClick={onClick}
      sx={{
        background: isSelected ? "var(--success)" : "var(--light-grey)",
        borderRadius: "6px",
        opacity: 0.8,
        flex: 1,
        p: "12px",
        cursor: "pointer",
      }}
    >
      {isSelected && <Check sx={{ fontSize: "18px", display: "flex", m: "0 0 -18px auto", color }} />}

      <Typography
        fontWeight="bold"
        sx={colorSx}
      >
        {option.title}
      </Typography>

      <Typography
        variant="body2"
        sx={colorSx}
      >
        {option.description}
      </Typography>

      {option.subText && (
        <Typography
          variant="body2"
          sx={colorSx}
        >
          {option.subText}
        </Typography>
      )}

      {option.type === TRANSPORTATION_TYPE.PICKUP && !!warehouse?.detailAddress && (
        <Typography
          variant="body2"
          mb="12px"
          fontWeight="bold"
          sx={colorSx}
        >
          {`${warehouse.detailAddress}, ${warehouse?.ward?.path}`}
        </Typography>
      )}
    </Box>
  );
};

const DeliveryInfo = () => {
  const {
    formState: { errors },
    watch,
    setValue,
    setError,
    clearErrors,
  } = useFormContext();
  const { shippingFee, pickupDate, pickUpType, warehouse, cod, hasCOD } = watch();
  const { data: paymentMethods } = usePaymentMethodList({
    limit: 200,
    skip: 0,
  });

  const { data: shippingProviders } = useTPList({
    limit: 200,
    skip: 0,
  });

  const handleChangeField = (name, value) => {
    setValue(name, value);
    if (!value) {
      setError(name, { message: "Vui lòng chọn ngày lấy hàng" });
    } else {
      clearErrors(name);
    }
  };

  const pickupDates = useMemo(() => {
    return Array.from({ length: 3 }, (_, index) => {
      const newDate = new Date();
      newDate.setDate(newDate.getDate() + index);
      return newDate;
    });
  }, []);

  const paymentMethodOptions = useMemo(() => {
    return paymentMethods?.data?.map((v) => ({
      label: v?.name,
      value: v?._id,
    }));
  }, [paymentMethods]);

  const shippingProviderOptions = useMemo(() => {
    return shippingProviders?.data?.map((v) => ({
      label: v?.name,
      value: v?._id,
    }));
  }, [shippingProviders]);

  useEffect(() => {
    if (pickupDate || !pickupDates?.length) return;
    const nextPickupDate = pickupDates[0].toISOString();
    setValue("pickupDate", nextPickupDate);
    clearErrors("pickupDate");
  }, [pickupDates, pickupDate, setValue, clearErrors]);

  return (
    <Stack
      gap="12px"
      sx={{ mt: "12px" }}
    >
      <Typography
        fontWeight="bold"
        mb="12px"
      >
        <RequiredLabel>Chọn ngày lấy hàng</RequiredLabel>
      </Typography>
      <Box>
        <Box
          display="flex"
          gap="12px"
        >
          {pickupDates?.map((v, i) => {
            return (
              <Button
                endIcon={pickupDate?.includes(v?.toISOString()?.slice(0, 10)) && <CheckCircle color="var(--success-txt)" />}
                onClick={() => handleChangeField("pickupDate", v?.toISOString())}
                sx={{
                  borderRadius: "100px",
                  color: pickupDate?.includes(v?.toISOString()?.slice(0, 10)) ? "var(--success-txt) !important" : "var(--black)",
                  borderColor: pickupDate?.includes(v?.toISOString()?.slice(0, 10)) ? "var(--success-txt) !important" : "var(--black)",
                }}
                variant="outlined"
                key={i}
              >
                {dayjs(v).format("dddd") + " - " + dayjs(v).format("DD-MM-YYYY")}
              </Button>
            );
          })}
        </Box>
        {errors.pickupDate && (
          <Typography
            variant="body2"
            color="red"
          >
            {errors.pickupDate.message}
          </Typography>
        )}
      </Box>
      <Box my="12px">
        <Stack
          direction="row"
          gap="12px"
        >
          {PICK_UP_OPTIONS.map((option) => (
            <PickUpCard
              key={option.type}
              option={option}
              isSelected={pickUpType === option.type}
              onClick={() => handleChangeField("pickUpType", option.type)}
              warehouse={warehouse}
            />
          ))}
        </Stack>
        {errors.pickUpType && (
          <Typography
            variant="body2"
            color="red"
          >
            {errors.pickUpType.message}
          </Typography>
        )}
      </Box>
      <FormControlAutocomplete
        name="shippingProvider"
        label="Đơn vị vận chuyển"
        options={shippingProviderOptions}
        placeholder="Đơn vị vận chuyển"
      />

      <Stack
        direction="row"
        gap="12px"
        sx={{ mt: "12px" }}
      >
        <FormControlAutocomplete
          name="paymentMethod"
          label="Hình thức thanh toán"
          options={paymentMethodOptions}
          placeholder="Hình thức thanh toán"
          required
          sx={{ flex: 1 }}
        />
        <Box sx={{ flex: 1 }}>
          <FormControlNumericInput
            name="shippingFee"
            label="Phí ship"
            prefix="đ"
            thousandSeparator
          />
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", fontStyle: "italic" }}
          >
            ({!!shippingFee ? convertCurrency(shippingFee) : 0} đồng)
          </Typography>
        </Box>
      </Stack>
      <RadioGroup row>
        <FormControlLabel
          value="COD"
          checked={hasCOD === "COD"}
          onChange={(e) => handleChangeField("hasCOD", e.target.value)}
          control={<Radio />}
          label="COD"
        />
        <FormControlLabel
          value="noCOD"
          checked={hasCOD === "noCOD"}
          onChange={(e) => handleChangeField("hasCOD", e.target.value)}
          control={<Radio />}
          label="Không COD"
        />
      </RadioGroup>
      {hasCOD === "COD" && (
        <>
          <FormControlNumericInput
            name="cod"
            label="COD"
            prefix="đ"
            thousandSeparator
            required
          />
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", fontStyle: "italic" }}
          >
            ({!!cod ? convertCurrency(cod) : 0} đồng)
          </Typography>
        </>
      )}
    </Stack>
  );
};

export default DeliveryInfo;
